import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { State } from 'reducers';
import { DeviceClass, DeviceStatus, Theme } from 'shared/models';
import { getBuildingByUrlSlug, getBuildingDevice, getBuildingThemes, getIsLoadingBuildingThemes, HasDeviceId, HasUrlSlug } from 'selectors/buildingsSelectors';
import { ErrorMessage } from 'ui/shared/messages';
import { QuickDeviceCommands } from '../quickDeviceCommands/QuickDeviceCommands';
import { LegacyEditDeviceDialog } from 'ui/devices/legacyEditDeviceDialog/LegacyEditDeviceDialog';
import { useCheckIns } from 'hooks/useCheckIns/useCheckIns';
import { parseCheckInData } from '../checkInCharts/parseCheckInData';
import { CheckInDetails, isSuspiciousStartupTime } from '../checkInDetails/CheckInDetails';
import { CheckInCharts } from '../checkInCharts/CheckInCharts';
import { useDeviceTypes, useEcsBuilding, useSession } from 'hooks';
import { EditDeviceDialog } from 'ui/devices/editDeviceDialog/EditDeviceDialog';
import { DebugLink } from 'ui/shared/DebugLink';
import { getBuildingThemesRequest } from 'actions/buildingActions';
import { useEffect } from 'react';
import { doesDeviceTypeSupportDeviceConfig } from 'logic/doesDeviceTypeSupportDeviceConfig';
import { getDeviceThemeHistory } from "selectors/themeHistorySelectors";

require('./DeviceStatusPage.css');

const quarterHour = 900000;

export const DeviceStatusPage = () => {
    const { t } = useTranslation();
    const location = useLocation();

    const { hasFeature } = useSession();

    const { deviceTypes } = useDeviceTypes();

    // Note, have to get buildingId from urlSlug instead of device
    // because the device might not be loaded yet.
    const match = useRouteMatch<HasUrlSlug & HasDeviceId>('/buildings/:urlSlug/devices/:deviceId/status')!;
    const { deviceId } = match.params;
    
    const device = useSelector((state: State) => getBuildingDevice(state, match.params));
    const building = useSelector((state: State) => getBuildingByUrlSlug(state, match.params));
    const buildingId = building?.id;
    const timeZone = building?.timeZone || 'UTC';
    const deviceType = device && deviceTypes && deviceTypes[device.deviceTypeId];
    const originalScript = (device && device.originalScript) || null;

    const themes = useSelector((state: State) => buildingId ? getBuildingThemes(state, { buildingId }) : {});
    const isLoadingThemes = useSelector((state: State) => buildingId ? getIsLoadingBuildingThemes(state, { buildingId }) : false);

    const { checkIns, isLoading, error: checkInsError } = useCheckIns(deviceId);
    const themeHistory = useSelector((state: State) => deviceId ? getDeviceThemeHistory(state, { deviceId }) : null);

    const dispatch = useDispatch();
    
    // Load building themes if they're not loaded yet
    useEffect(() => {
        if (buildingId && !themes && !isLoadingThemes) {
            dispatch(getBuildingThemesRequest(buildingId));
        }
    }, [ buildingId, themes, isLoadingThemes ]);

    // the checkins come in newest-first order, and a human-readable format.
    // So switch to JavaScript timestamp and sort by oldest first for the charts.
    const lastCheckIn = checkIns && checkIns[0];

    const lastCheckInData = lastCheckIn ? parseCheckInData(lastCheckIn.data, lastCheckIn.utcTime) : null;
    
    const lastCheckInTime = lastCheckInData?.CurrentTime || null;
    const lastCheckInStartupTime = lastCheckInData?.StartupTime || null;

    let dataError = checkInsError ? t([ `ERROR.${checkInsError.message}`, 'SERVER_ERROR' ]) : null;
    if (lastCheckInTime! > Date.now()) {
        dataError = dataError || t('DEVICE.STATUS.ERROR_INVALID_LAST_DATE');
    }

    const deviceServerTimeDelta = (lastCheckIn?.utcTime || 0) - (lastCheckInTime || 0);

    const adjustedStartupTime = lastCheckInStartupTime ? lastCheckInStartupTime + deviceServerTimeDelta : null;

    const adjustedLastCheckInTime = lastCheckInTime ? lastCheckInTime + deviceServerTimeDelta : null;
    const nextCheckInTime = adjustedLastCheckInTime ? (adjustedLastCheckInTime + quarterHour) : null;
    const aheadOrBehind = deviceServerTimeDelta > 0 ? 'BEHIND' : 'AHEAD' ;

    const inaccurateDeviceClockWarning =
        Math.abs(deviceServerTimeDelta) >= 60000
            ? t(`DEVICE.STATUS.ERROR_DEVICE_CLOCK_${aheadOrBehind}`, [ moment.duration(deviceServerTimeDelta).humanize() ])
            : null;

    const startupTimeWarning =
        lastCheckInData
        && device?.deviceClass === DeviceClass.Display
        && adjustedStartupTime && isSuspiciousStartupTime(adjustedStartupTime, timeZone)
            ? t('DEVICE.STATUS.WARNING_SUSPICIOUS_STARTUP_TIME')
            : null;

    const themeId = themeHistory ? themeHistory.themeId : null;

    const theme: Theme | null = themeId ? themes && themes[themeId] : null;
    const themeUrl = themeId ? `/buildings/${buildingId}/themes/${themeId}/edit` : '';

    const supportsDeviceConfig = doesDeviceTypeSupportDeviceConfig(deviceType);
    const hasDeviceStatus =
        device?.deviceClass !== DeviceClass.Modem &&
        device?.deviceClass !== DeviceClass.ATA &&
        device?.deviceClass !== DeviceClass.SIMCard;
    
    const {
        isEcsEnabled,
    } = useEcsBuilding(buildingId || null);

    if (!buildingId) {
        return null;
    }
    return (
        <div id="device-status-page" className="page">
            { device &&
                <QuickDeviceCommands
                    isEcsEnabled={ isEcsEnabled || false }
                    buildingId={ buildingId }
                    deviceId={ deviceId }
                    isDown={ device.status === DeviceStatus.Down }
                    nextCheckIn={ nextCheckInTime }
                    deviceType={ deviceType }
                    originalScript={ originalScript }
                />
            }
            {
                hasDeviceStatus &&
                <>
                    <h2>
                        { t('DEVICE.STATUS.TITLE') }
                    </h2>
                    <DebugLink
                        sql={
                            `--[Device]\n` +
                            `SELECT * FROM devices WHERE id = '${deviceId}';\n\n` +
                            `--[Status]\n` +
                            `SELECT device_id, status, last_check_in, ip_address, firmware, theme_etag, theme_name, theme_published FROM device_status WHERE device_id = '${deviceId}';\n\n` +
                            `--[Last Checkin]\n` +
                            `SELECT r.* FROM device_status ds, json_each(report) r WHERE ds.device_id = '${deviceId}';\n\n` +
                            `--[Last Checkin (Raw)]\n` +
                            `SELECT body FROM device_status WHERE device_id = '${deviceId}';\n`
                        }
                    />
                    <ErrorMessage message={ dataError || inaccurateDeviceClockWarning || startupTimeWarning }/>
                    <CheckInDetails
                        isLoading={ isLoading && !lastCheckIn }
                        timeZone={ timeZone }
                        checkInData={ lastCheckInData }
                        lastCheckIn={ adjustedLastCheckInTime }
                        nextCheckIn={ nextCheckInTime }
                        deviceServerTimeDelta={ deviceServerTimeDelta }
                        isTimeOutOfSync={ inaccurateDeviceClockWarning !== null }
                        theme={ theme }
                        themeUrl={ themeUrl }
                        themeHistory={ themeHistory }
                    />
                    <CheckInCharts
                        isLoading={ isLoading && !lastCheckIn }
                        checkIns={ checkIns || [] }
                        timeZone={ timeZone }
                    />
                </>
            }
            { supportsDeviceConfig || !hasDeviceStatus
                ? <EditDeviceDialog visible={ /\/buildings\/.+\/devices\/.+\/status\/edit$/.test(location.pathname) }/>
                : <LegacyEditDeviceDialog visible={ /\/buildings\/.+\/devices\/.+\/status\/edit$/.test(location.pathname) }/>
            }
        </div>
    );
};
